'use client';

import type { ReactNode } from 'react';

import { motion } from 'motion/react';

import { cn, FOCUS_RING } from '@/lib/utils';

const SPRING = { type: 'spring', stiffness: 420, damping: 34, mass: 0.7 } as const;

/**
 * `tabs` is the recessed track with a raised thumb; `chips` is the free-standing pill row. Both
 * move one indicator between options rather than blinking it off here and on over there.
 */
type Variant = 'tabs' | 'chips';

const STYLES: Record<Variant, { root: string; option: string; on: string; off: string }> = {
  tabs: {
    // dark:bg-background, because --muted and --card are the same value in dark mode and this
    // sits on a card — the track simply vanished. Going darker than the card reads as the
    // recessed well it is meant to be, in both themes.
    root: 'isolate inline-flex rounded-lg bg-muted p-[3px] dark:bg-background',
    // border-transparent so the shared focus style has something to colour in. `chips` already
    // carries a real border.
    option: 'rounded-md border border-transparent px-2.5 py-1 text-xs font-medium',
    on: 'text-foreground',
    off: 'text-muted-foreground hover:text-foreground',
  },
  chips: {
    root: 'isolate flex flex-wrap gap-2',
    option: 'rounded-full border px-3.5 py-1.5 text-sm font-medium',
    on: 'border-transparent text-background',
    off: 'bg-card text-muted-foreground hover:text-foreground',
  },
};

/**
 * The thumb. Square-cornered on `chips` because the pill's own radius is on the indicator.
 *
 * It travels between the two layers of the row. The isolated root gives it one stacking context to
 * sit in: above the options' own fills, so it stays visible crossing the chips between where it was
 * and where it is going, and below their labels, which are `z-10` and stay legible as it passes
 * under them. Either side of that reads as broken — over the labels it wipes them out, under the
 * fills it disappears mid-flight and reappears at the far end.
 */
const INDICATOR: Record<Variant, string> = {
  // bg-card, not bg-background: it has to differ from the track under it, and the track is
  // --background in dark. --card clears both (white on muted, #252320 on #1c1b19).
  tabs: 'rounded-md bg-card shadow-sm',
  chips: '-inset-px rounded-full bg-foreground',
};

type SegmentedControlProps<T extends string> = {
  options: readonly T[];
  value: T;
  onChange: (next: T) => void;
  /** Motion matches shared layout by layoutId across the whole document — two controls on one
   *  page need different scopes or they fight over one indicator. */
  scope: string;
  label: string;
  variant?: Variant;
  /** Hovered or focused option, for callers that preload on intent. */
  onIntent?: (option: T | null) => void;
  renderOption?: (option: T) => ReactNode;
  className?: string;
};

export function SegmentedControl<T extends string>({
  options,
  value,
  onChange,
  scope,
  label,
  variant = 'tabs',
  onIntent,
  renderOption,
  className,
}: SegmentedControlProps<T>) {
  const styles = STYLES[variant];

  return (
    <span role="group" aria-label={label} className={cn(styles.root, className)}>
      {options.map(option => {
        const selected = option === value;
        return (
          <button
            key={option}
            type="button"
            aria-pressed={selected}
            onClick={() => onChange(option)}
            onMouseEnter={() => onIntent?.(option)}
            onMouseLeave={() => onIntent?.(null)}
            onFocus={() => onIntent?.(option)}
            onBlur={() => onIntent?.(null)}
            className={cn(
              'relative inline-flex cursor-pointer items-center justify-center gap-1.5 transition-colors duration-200',
              FOCUS_RING,
              styles.option,
              selected ? styles.on : styles.off,
            )}
          >
            {selected && (
              <motion.span
                layoutId={`${scope}-indicator`}
                aria-hidden="true"
                transition={SPRING}
                className={cn('absolute inset-0 z-1', INDICATOR[variant])}
              />
            )}
            <span className="relative z-10 inline-flex items-center gap-1.5">
              {renderOption ? renderOption(option) : option}
            </span>
          </button>
        );
      })}
    </span>
  );
}
